import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { AlertTriangle, Building2, Check, List } from 'lucide-react';
import { getAuthToken } from '../../auth/context/AuthContext';
import PasswordField from '../../../shared/components/PasswordField';
import ModalError from '../../../shared/components/ModalError';
import Pagination, { pageSlice } from '../../../shared/components/Pagination';

/**
 * The per-company actions, behind one button.
 *
 * Seven buttons on every row read as a wall and pushed the table sideways, and
 * the dangerous ones sat in the same grey as the routine ones. Logging in stays
 * outside as the one people reach for; the rest live here, with Delete kept
 * apart at the bottom.
 *
 * The menu is positioned fixed off the button's own rectangle: the table
 * scrolls sideways, and anything absolute inside it gets clipped.
 */
// Breathing room between the button and the menu, and between the menu and
// the edge of the window.
const GAP = 4;
const EDGE = 8;

function RowMenu({ items, label = 'Actions' }) {
  const [at, setAt] = useState(null);
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  const close = useCallback(() => setAt(null), []);

  useEffect(() => {
    if (!at) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    const onDown = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      close();
    };
    // Scrolling would leave the menu behind, pointing at nothing.
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onDown);
    const onScroll = (e) => { if (!menuRef.current?.contains(e.target)) close(); };
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', close);
    };
  }, [at, close]);

  const open = () => {
    const r = btnRef.current.getBoundingClientRect();
    // Opens downward; the effect below flips it up when the row sits too near
    // the bottom of the window for the menu to fit.
    setAt(at ? null : { top: r.bottom + GAP, right: window.innerWidth - r.right, anchor: r });
  };

  // Measured rather than guessed: the menu's height depends on how many items
  // this row has, and a row at the foot of a long table would otherwise push
  // the list off the bottom of the screen and over the pagination.
  useLayoutEffect(() => {
    const menu = menuRef.current;
    if (!at || !menu) return;
    const height = menu.offsetHeight;
    const room = window.innerHeight - at.anchor.bottom - GAP;
    const top = height <= room
      ? at.anchor.bottom + GAP
      : Math.max(EDGE, at.anchor.top - height - GAP);
    if (Math.round(top) !== Math.round(at.top)) setAt((a) => ({ ...a, top }));
  }, [at]);

  return (
    <>
      <button
        ref={btnRef}
        className="btn btn-sm"
        onClick={open}
        aria-haspopup="menu"
        aria-expanded={at ? 'true' : 'false'}
        aria-label={label}
        title={label}
        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '6px 9px' }}
      >
        <List size={15} strokeWidth={2} />
      </button>
      {at && (
        <div
          ref={menuRef}
          role="menu"
          style={{
            position: 'fixed', top: at.top, right: at.right, zIndex: 1000,
            minWidth: 190, padding: 5,
            // A menu taller than the window scrolls inside itself rather than
            // spilling past either edge.
            maxHeight: `calc(100vh - ${EDGE * 2}px)`, overflowY: 'auto',
            background: 'var(--bg-surface, #fff)',
            border: '1px solid var(--border, #e5e7eb)',
            borderRadius: 10,
            boxShadow: '0 12px 32px rgba(16, 24, 40, 0.14)',
          }}
        >
          {items.filter(Boolean).map((item, i) => (
            <React.Fragment key={item.label}>
              {item.danger && i > 0 && (
                <div style={{ height: 1, background: 'var(--border, #e5e7eb)', margin: '5px 0' }} />
              )}
              <button
                role="menuitem"
                className="row-menu-item"
                disabled={item.disabled}
                title={item.title || ''}
                onClick={() => { close(); item.onClick(); }}
                style={{
                  display: 'block', width: '100%', textAlign: 'left',
                  padding: '7px 10px', border: 0, borderRadius: 7,
                  background: 'transparent', font: 'inherit', fontSize: 13,
                  color: item.danger ? 'var(--danger, #dc2626)' : 'var(--text-primary, #101828)',
                  cursor: item.disabled ? 'not-allowed' : 'pointer',
                  opacity: item.disabled ? 0.45 : 1,
                }}
              >
                {item.label}
              </button>
            </React.Fragment>
          ))}
        </div>
      )}
    </>
  );
}

const API = `${process.env.REACT_APP_API_URL || 'https://recruitai.pingitright.com/api'}/platform`;

const authHeaders = () => ({
  Authorization: `Bearer ${getAuthToken()}`,
  'Content-Type': 'application/json',
});

/**
 * The page-level error banner sits behind the modal backdrop, so a failure
 * raised while a modal is open — a duplicate admin email, most often — was
 * invisible: the dialog just sat there looking like nothing had happened.
 * Modals render the message themselves.
 */
export default function Companies() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [err, setErr]             = useState('');
  const [msg, setMsg]             = useState('');
  const [search, setSearch]       = useState('');
  const [page, setPage]           = useState(1);
  const [busy, setBusy]           = useState(false);

  const paged = pageSlice(companies, page);

  const [showAdd, setShowAdd] = useState(false);
  const [loginFor, setLoginFor] = useState(null);   // company we are about to sign in as
  const [deleteFor, setDeleteFor] = useState(null); // company we are about to delete
  const [adminFor, setAdminFor] = useState(null);   // company we are adding an admin to
  const [adminForm, setAdminForm] = useState({ name: '', email: '', password: '' });
  const [walletFor, setWalletFor] = useState(null);
  const [walletForm, setWalletForm] = useState({ amount: '' });
  const [generalMarkup, setGeneralMarkup] = useState(10);
  const [markupFor, setMarkupFor] = useState(null);
  const [markupValue, setMarkupValue] = useState('');
  const emptyForm = {
    company_name: '', admin_name: '', admin_email: '', admin_password: '',
    currency: 'USD',
    address_line1: '', address_line2: '', city: '', state: '', country: '', pincode: '',
    tax_id: '',
  };
  const [form, setForm] = useState(emptyForm);


  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const res = await fetch(`${API}/companies?search=${encodeURIComponent(search)}`, { headers: authHeaders() });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || `Failed to load companies (${res.status})`);
      }
      const data = await res.json();
      setCompanies(data.companies || []);
      if (data.ai_markup != null) setGeneralMarkup(data.ai_markup);
    } catch (e) { setErr(e.message); }
    setLoading(false);
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(''), 3500); };

  const addCompany = async (e) => {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      const res = await fetch(`${API}/companies`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ ...form, send_welcome: true }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Could not create company');
      setShowAdd(false);
      setForm(emptyForm);
      flash(`Created ${d.name} with admin ${d.admin.email}. Wallet ${d.wallet_balance_display || ''}`);
      load();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  const loginAsCompany = (c) => {
    setLoginFor(null);
    // A new tab, so this one keeps the platform session. The tab mints its own
    // token on arrival — nothing sensitive travels in the URL.
    window.open(`/impersonate/${c.id}`, '_blank', 'noopener');
  };

  const toggleFrozen = async (c) => {
    setErr('');
    try {
      const res = await fetch(`${API}/companies/${c.id}`, {
        method: 'PATCH', headers: authHeaders(), body: JSON.stringify({ wallet_frozen: !c.wallet_frozen }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Could not update wallet');
      flash(`${c.name} wallet ${c.wallet_frozen ? 'unfrozen' : 'frozen'}`);
      load();
    } catch (e) { setErr(e.message); }
  };

  const openWallet = (c) => {
    setErr('');
    setWalletFor(c);
    setWalletForm({ amount: '' });
  };

  const openMarkup = (c) => {
    setErr('');
    setMarkupFor(c);
    setMarkupValue(c.ai_markup == null ? '' : String(c.ai_markup));
  };

  const saveMarkup = async (e) => {
    e.preventDefault();
    setBusy(true); setErr('');
    const trimmed = markupValue.trim();
    if (trimmed !== '' && !/^\d+$/.test(trimmed)) {
      setErr('AI markup must be a whole number from 1 to 100');
      setBusy(false);
      return;
    }
    try {
      const res = await fetch(`${API}/companies/${markupFor.id}`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({ ai_markup: trimmed === '' ? null : Number(trimmed) }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(typeof d.detail === 'string' ? d.detail : 'Could not save AI markup');
      const name = markupFor.name;
      setMarkupFor(null);
      flash(d.ai_markup == null
        ? `${name} uses the platform AI markup (${generalMarkup}×).`
        : `${name} AI markup is ${d.ai_markup}×.`);
      load();
    } catch (error) { setErr(error.message); }
    setBusy(false);
  };

  const updateWallet = async (e) => {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      const res = await fetch(`${API}/companies/${walletFor.id}/wallet`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ amount: walletForm.amount.trim() }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(typeof d.detail === 'string' ? d.detail : 'Could not add credits');
      const name = walletFor.name;
      setWalletFor(null);
      setWalletForm({ amount: '' });
      flash(`Added ${d.amount_display} to ${name}. Wallet is now ${d.wallet_balance_display}`);
      load();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  const toggleActive = async (c) => {
    setErr('');
    try {
      const res = await fetch(`${API}/companies/${c.id}`, {
        method: 'PATCH', headers: authHeaders(), body: JSON.stringify({ is_active: !c.is_active }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Update failed');
      flash(`${c.name} ${c.is_active ? 'deactivated' : 'reactivated'}`);
      load();
    } catch (e) { setErr(e.message); }
  };

  const addAdmin = async (e) => {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      const res = await fetch(`${API}/companies/${adminFor.id}/admins`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ ...adminForm, send_welcome: true }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Could not add admin');
      setAdminFor(null); setAdminForm({ name: '', email: '', password: '' });
      flash(`Added admin ${d.email}`);
      load();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  const removeCompany = async (c) => {
    setDeleteFor(null);
    setErr('');
    setBusy(true);
    try {
      const res = await fetch(`${API}/companies/${c.id}`, { method: 'DELETE', headers: authHeaders() });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Delete failed');
      flash(d.message);
      load();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div className="page-title">Companies</div>
          <div className="page-subtitle">Customer companies on the platform</div>
        </div>
        <button className="btn btn-primary" onClick={() => { setErr(''); setShowAdd(true); }}>+ New Company</button>
      </div>

      {err && !showAdd && !adminFor && !deleteFor && !loginFor && !walletFor && !markupFor && <div className="alert alert-error" style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6 }}><AlertTriangle size={14} strokeWidth={2} /> {err}</div>}
      {msg && <div className="alert alert-success" style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6 }}><Check size={14} strokeWidth={2.5} /> {msg}</div>}

      <input
        className="form-input"
        placeholder="Search by name or code…"
        value={search}
        onChange={e => { setSearch(e.target.value); setPage(1); }}
        style={{ maxWidth: 320, marginBottom: 16 }}
      />

      {loading ? (
        <div className="empty-state"><div className="empty-state-text">Loading…</div></div>
      ) : companies.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon"><Building2 size={40} strokeWidth={1.5} /></div>
          <div className="empty-state-title">No companies yet</div>
          <div className="empty-state-text">Create your first customer company.</div>
        </div>
      ) : (
        <div className="card" style={{ overflowX: 'auto' }}>
          <table className="table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Company</th><th>Code</th><th>Currency</th><th>Wallet</th><th>AI markup</th><th>Admins</th><th>Users</th><th>Jobs</th><th>Candidates</th><th>Status</th><th />
              </tr>
            </thead>
            <tbody>
              {paged.shown.map(c => (
                <tr key={c.id}>
                  <td style={{ fontWeight: 600 }}>{c.name}</td>
                  <td style={{ fontFamily: 'var(--font-mono, monospace)', fontWeight: 700, letterSpacing: '0.04em' }}>{c.code || '—'}</td>
                  <td style={{ fontSize: 13, fontWeight: 600 }}>{c.currency || 'USD'}</td>
                  <td style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                    {c.wallet_balance_display || '—'}
                    {c.wallet_frozen ? <span style={{ marginLeft: 6, color: 'var(--danger, #b91c1c)', fontSize: 11, fontWeight: 700 }}>Frozen</span> : null}
                  </td>
                  <td style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                    {c.ai_markup == null ? `General (${generalMarkup}×)` : `${c.ai_markup}×`}
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    {c.admins.length ? c.admins.map(a => a.email).join(', ') : <em>none</em>}
                  </td>
                  <td>{c.user_count}</td>
                  <td>{c.job_count}</td>
                  <td>{c.candidate_count}</td>
                  <td>
                    <span style={{ color: c.is_active ? 'var(--success)' : 'var(--text-muted)', fontSize: 12, fontWeight: 600 }}>
                      {c.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <RowMenu
                      items={[
                        {
                          label: 'Log in as this company',
                          onClick: () => { setErr(''); setLoginFor(c); },
                          disabled: !c.is_active || c.admins.length === 0,
                          title: !c.is_active
                            ? 'Company is deactivated — reactivate it first'
                            : c.admins.length === 0
                              ? 'No admin to sign in as — add one first'
                              : `Log in as ${c.name}`,
                        },
                        { label: 'Add admin', onClick: () => { setErr(''); setAdminFor(c); } },
                        { label: 'Add credits', onClick: () => openWallet(c) },
                        { label: 'AI markup', onClick: () => openMarkup(c) },
                        { label: c.wallet_frozen ? 'Unfreeze wallet' : 'Freeze wallet', onClick: () => toggleFrozen(c) },
                        { label: c.is_active ? 'Deactivate company' : 'Activate company', onClick: () => toggleActive(c) },
                        {
                          label: 'Delete company',
                          danger: true,
                          disabled: c.job_count > 0,
                          title: c.job_count > 0 ? 'Company owns job posts — deactivate instead' : 'Delete company',
                          onClick: () => { setErr(''); setDeleteFor(c); },
                        },
                      ]}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <Pagination {...paged} noun="companies" onChange={setPage} />

        </div>
      )}

      {/* New company */}
      {showAdd && (
        <div className="modal-backdrop">
          <div className="modal modal-wide">
            <h3>New Company</h3>
            <div className="modal-sub">Creates the company and its first admin together.</div>
            <form onSubmit={addCompany}>
              <div className="form-group">
                <label className="form-label">Company name</label>
                <input className="form-input" required value={form.company_name} autoFocus
                  onChange={e => setForm({ ...form, company_name: e.target.value })} placeholder="Acme Corp" />
              </div>
              <div className="form-group">
                <label className="form-label">Currency</label>
                <select className="form-input" required value={form.currency}
                  onChange={e => setForm({ ...form, currency: e.target.value })}>
                  <option value="INR">INR</option>
                  <option value="USD">USD</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Wallet</label>
                <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                  {form.currency === 'INR' ? 'Indian rupees. Starts with ₹500 once.' : 'US dollars. Starts with $10 once.'}
                </div>
              </div>

              <div className="modal-section">First admin</div>

              <div className="form-group">
                <label className="form-label">Name</label>
                <input className="form-input" required value={form.admin_name}
                  onChange={e => setForm({ ...form, admin_name: e.target.value })} placeholder="Jane Doe" />
              </div>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input className="form-input" type="email" required value={form.admin_email}
                  onChange={e => setForm({ ...form, admin_email: e.target.value })} placeholder="jane@acme.com" />
              </div>
              <div className="form-group">
                <label className="form-label">Temporary password</label>
                <PasswordField
                  value={form.admin_password}
                  onChange={v => setForm({ ...form, admin_password: v })}
                  label=""
                />
              </div>

              <div className="form-group">
                <label className="form-label">Address line 1</label>
                <input className="form-input" required value={form.address_line1}
                  onChange={e => setForm({ ...form, address_line1: e.target.value })} placeholder="Street, building" />
              </div>
              <div className="form-group">
                <label className="form-label">Address line 2</label>
                <input className="form-input" value={form.address_line2}
                  onChange={e => setForm({ ...form, address_line2: e.target.value })} placeholder="Optional" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 12px' }}>
                <div className="form-group">
                  <label className="form-label">City</label>
                  <input className="form-input" required value={form.city}
                    onChange={e => setForm({ ...form, city: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">State</label>
                  <input className="form-input" required value={form.state}
                    onChange={e => setForm({ ...form, state: e.target.value })} />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 12px' }}>
                <div className="form-group">
                  <label className="form-label">Country</label>
                  <input className="form-input" required value={form.country}
                    onChange={e => setForm({ ...form, country: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Pincode</label>
                  <input className="form-input" required value={form.pincode}
                    onChange={e => setForm({ ...form, pincode: e.target.value })} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Tax ID</label>
                <input className="form-input" value={form.tax_id} maxLength={40}
                  onChange={e => setForm({ ...form, tax_id: e.target.value })}
                  placeholder="GSTIN, EIN, or VAT. Optional" />
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                  Optional. For an Indian company, a GSTIN decides SGST and CGST or IGST. Leave it blank to use the state.
                </div>
              </div>

              <div className="modal-hint">
                This admin can then add managers, team leads and recruiters themselves.
              </div>
              <ModalError message={err} />
              <div className="modal-actions">
                <button type="button" className="btn" onClick={() => { setErr(''); setShowAdd(false); }}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={busy}>
                  {busy ? 'Creating…' : 'Create Company'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add admin to existing company */}

      {/* window.confirm is blocked in some embedded browsers, where it returns
          false and the click silently does nothing. An in-app modal always works. */}
      {loginFor && (
        <div className="modal-backdrop">
          <div className="modal">
            <h3>Sign in as {loginFor.name}?</h3>
            <div className="modal-sub">
              You will leave your platform account and be signed in as this company's admin.
            </div>
            <ul style={{ fontSize: 13.5, color: 'var(--text-muted)', lineHeight: 1.7, paddingLeft: 18, margin: '0 0 4px' }}>
              <li>The session lasts 60 minutes.</li>
              <li>It is recorded against your name.</li>
              <li>A banner will show you how to return to the platform.</li>
            </ul>
            <ModalError message={err} />
            <div className="modal-actions">
              <button type="button" className="btn" onClick={() => { setErr(''); setLoginFor(null); }}>Cancel</button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={busy}
                onClick={() => loginAsCompany(loginFor)}
              >
                {busy ? 'Signing in…' : `Sign in as ${loginFor.name}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {markupFor && (
        <div className="modal-backdrop">
          <div className="modal">
            <h3>AI markup</h3>
            <div className="modal-sub">
              {markupFor.name} is charged this many times the measured AI cost.
              Leave blank to use the platform markup ({generalMarkup}×).
              Charges already on the wallet stay as they are.
            </div>
            <form onSubmit={saveMarkup}>
              <div className="form-group">
                <label className="form-label">Multiplier</label>
                <input
                  className="form-input"
                  autoFocus
                  inputMode="numeric"
                  min="1"
                  max="100"
                  placeholder={`${generalMarkup}`}
                  value={markupValue}
                  onChange={e => setMarkupValue(e.target.value)}
                  style={{ maxWidth: 160 }}
                />
              </div>
              <ModalError message={err} />
              <div className="modal-actions">
                <button type="button" className="btn" onClick={() => { setErr(''); setMarkupFor(null); }}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={busy}>
                  {busy ? 'Saving…' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {walletFor && (
        <div className="modal-backdrop">
          <div className="modal">
            <h3>Add credits</h3>
            <div className="modal-sub">
              {walletFor.name} is at {walletFor.wallet_balance_display || '$0.00'}.
              This amount is added on top. The current balance stays as it is.
            </div>
            <form onSubmit={updateWallet}>
              <div className="form-group">
                <label className="form-label">Amount</label>
                <input
                  className="form-input"
                  required
                  autoFocus
                  inputMode="decimal"
                  placeholder="0.00"
                  value={walletForm.amount}
                  onChange={e => setWalletForm({ ...walletForm, amount: e.target.value })}
                />
              </div>
              <div className="modal-hint">
                A recharge order is saved with the message credits. It is not a Razorpay payment.
              </div>
              <ModalError message={err} />
              <div className="modal-actions">
                <button type="button" className="btn" onClick={() => { setErr(''); setWalletFor(null); }}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={busy}>
                  {busy ? 'Adding…' : 'Add credits'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add an admin to an existing company. */}
      {adminFor && (
        <div className="modal-backdrop">
          <div className="modal">
            <h3>Add admin</h3>
            <div className="modal-sub">to {adminFor.name}</div>
            <form onSubmit={addAdmin}>
              <div className="form-group">
                <label className="form-label">Name</label>
                <input className="form-input" required value={adminForm.name} autoFocus
                  onChange={e => setAdminForm({ ...adminForm, name: e.target.value })} placeholder="Jane Doe" />
              </div>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input className="form-input" type="email" required value={adminForm.email}
                  onChange={e => setAdminForm({ ...adminForm, email: e.target.value })} placeholder="jane@acme.com" />
              </div>
              <div className="form-group">
                <label className="form-label">Temporary password</label>
                <PasswordField
                  value={adminForm.password}
                  onChange={v => setAdminForm({ ...adminForm, password: v })}
                  label=""
                />
              </div>
              <ModalError message={err} />
              <div className="modal-actions">
                <button type="button" className="btn" onClick={() => { setErr(''); setAdminFor(null); }}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={busy}>
                  {busy ? 'Adding…' : 'Add Admin'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Deleting is irreversible, so the count of what goes with it is stated plainly. */}
      {deleteFor && (
        <div className="modal-backdrop">
          <div className="modal">
            <h3>Delete {deleteFor.name}?</h3>
            <div className="modal-sub">This cannot be undone.</div>
            <p style={{ fontSize: 14, color: 'var(--text-muted)', lineHeight: 1.65, margin: 0 }}>
              The company and its <strong>{deleteFor.user_count}</strong>{' '}
              {deleteFor.user_count === 1 ? 'user' : 'users'} will be permanently removed.
              Anyone signed in as one of them loses access immediately.
            </p>
            <ModalError message={err} />
            <div className="modal-actions">
              <button type="button" className="btn" onClick={() => { setErr(''); setDeleteFor(null); }}>Cancel</button>
              <button
                type="button"
                className="btn btn-danger"
                disabled={busy}
                onClick={() => removeCompany(deleteFor)}
              >
                {busy ? 'Deleting…' : `Delete ${deleteFor.name}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
